import React, { useState, useEffect, useMemo } from "react";
import {
  NCXFormBoolean,
  NCXFormDate,
  NCXFormDivider,
  NCXFormInputField,
  NCXFormLabel,
  NCXFormMultiSelect,
  NCXFormRadioButton,
  NCXFormRateAgent,
  NCXFormRating,
  NCXFormSelect,
} from "./NCXDynamicFormComponents";
import ReCAPTCHA from "react-google-recaptcha";
import { Button } from "@mui/material";
import { s360ConfigService } from "../Services/S360Config";
import apiService from "../Services/APIService";
import {
  DATA_TEST_ID_PREFIX,
  LIVECHAT_CLICK_EVENTS,
} from "../../utils/Constants/statsig.constants";
import NCXPrivacyPolicyCompact from "../Conversation/MessageBoxTypes/NCXComponents/NCXPrivacyPolicyCompact";

const DynamicForm = ({
  data,
  handleChangeForm,
  actions,
  offline,
  offlineForm,
  agentInfo,
  decideTermsAndConditionRendering,
}) => {
  const [finalForm, setFinalForm] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [captchaSubmitted, setCaptchaSubmitted] = useState(false);
  const [offlineFormSubmitted, setOfflineFormSubmitted] = useState(false);
  const [validationRules, setValidationRules] = useState({});
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(
    localStorage.getItem(
      `nextiva-widget-terms-acceptance-${s360ConfigService?.config?.widgetconfigId}`
    ) === "true"
  );

  const termsAndConditionsDecider = useMemo(() => {
    const privacyPolicy =
      s360ConfigService.config.configDetails?.privacyPolicyDetails;
    if (s360ConfigService.config.sandBox) {
      return false;
    }
    if (!privacyPolicy?.enabled) return false;

    if (privacyPolicy.type === "compact" && !acceptedPrivacy) {
      return true;
    }

    return false;
  }, [
    s360ConfigService.config?.configDetails?.privacyPolicyDetails,
    acceptedPrivacy,
  ]);

  const configPrimaryColor =
    s360ConfigService?.config?.configDetails?.primaryColor || "#015fe";
  const configPrimaryFontColor =
    s360ConfigService.config?.configDetails?.primaryFontColor || "#fff";

  // Function to fetch validation rules for all fields
  const fetchValidationRules = async () => {
    const rules = {};

    const validationPromises = data[0].elements.map(async (field) => {
      const fieldname = field?.properties?.fieldname;
      if (fieldname && field?.properties?.validationId) {
        try {
          const regexData = await apiService.getRegexExpressionFromId(
            field.properties.validationId
          );

          let regexPattern = null;
          if (regexData?.data?.expression) {
            try {
              regexPattern = new RegExp(regexData.data.expression);
            } catch (regexError) {
              console.error(
                `Invalid regex pattern for ${fieldname}:`,
                regexError
              );
            }
          }

          rules[fieldname] = {
            regex: regexPattern,
            validationMessage:
              field?.properties?.validationMessage || "Invalid input",
            mandatory: field?.properties?.mandatory || false,
          };
        } catch (error) {
          console.error(
            `Error fetching validation rule for ${fieldname}:`,
            error
          );
          rules[fieldname] = {
            regex: null,
            validationMessage:
              field?.properties?.validationMessage || "Invalid input",
            mandatory: field?.properties?.mandatory || false,
          };
        }
      } else if (fieldname) {
        rules[fieldname] = {
          regex: null,
          validationMessage:
            field?.properties?.validationMessage || "Invalid input",
          mandatory: field?.properties?.mandatory || false,
        };
      }
    });

    await Promise.all(validationPromises);

    setValidationRules(rules);
  };

  useEffect(() => {
    if (s360ConfigService.config.sandBox) {
      return;
    }
    const initialForm = {};
    data[0].elements.forEach((field) => {
      if (field.type !== "panel" && field?.properties?.fieldname) {
        initialForm[field.properties.fieldname] =
          field?.properties?.defaultValue || "";
      }
    });

    setFinalForm(initialForm);

    fetchValidationRules();
  }, [data]);

  const validateField = (fieldname, value, field) => {
    const errors = { ...validationErrors };
    const fieldRules = validationRules[fieldname];

    if (!fieldRules) {
      return;
    }

    // Check mandatory validation first
    if (fieldRules.mandatory) {
      // For radio buttons, check if value is null, undefined, or empty string
      if (!value || value === "") {
        errors[fieldname] =
          fieldRules.validationMessage || "This field is required";
        setValidationErrors(errors);
        return;
      }
    }

    // Then check regex validation if applicable
    if (fieldRules.regex && value) {
      try {
        if (!fieldRules.regex.test(value)) {
          errors[fieldname] = fieldRules.validationMessage;
          setValidationErrors(errors);
          return;
        }
      } catch (regexError) {
        console.error(`Regex validation error for ${fieldname}:`, regexError);
      }
    }

    // If all validations pass, remove any existing errors
    delete errors[fieldname];
    setValidationErrors(errors);
  };

  const handleInputChange = (fieldname, value, field) => {
    setFinalForm((prev) => ({
      ...prev,
      [fieldname]: value,
    }));
    validateField(fieldname, value, field);
    handleChangeForm?.(fieldname, value);
  };

  const startChat = (finalForm) => {
    actions.startChat(finalForm);
  };

  const handleSubmit = async () => {
    if (s360ConfigService.config?.sandBox) return;

    const errors = {};
    let hasErrors = false;

    // First validate all mandatory fields
    data[0].elements.forEach((field) => {
      const fieldname = field?.properties?.fieldname;
      const value = finalForm[fieldname];
      const fieldRules = validationRules[fieldname];

      if (fieldname && fieldRules) {
        // Check if field is mandatory and empty
        if (fieldRules.mandatory && !value?.trim()) {
          errors[fieldname] =
            fieldRules.validationMessage ||
            `${field?.properties?.label || fieldname} is required`;
          hasErrors = true;
        }
      }
    });

    // If there are mandatory field errors, show them and return
    if (hasErrors) {
      setValidationErrors(errors);
      return;
    }

    // If all mandatory fields are filled, proceed with regex validation
    for (const field of data[0].elements) {
      const fieldname = field?.properties?.fieldname;
      const value = finalForm[fieldname];
      const fieldRules = validationRules[fieldname];

      if (
        fieldname &&
        fieldRules &&
        typeof value === "string" &&
        value?.trim()
      ) {
        if (fieldRules.regex && !fieldRules.regex.test(value)) {
          errors[fieldname] = fieldRules.validationMessage;
          hasErrors = true;
        }
      }
    }

    setValidationErrors(errors);

    // Only proceed if there are no errors
    if (!hasErrors) {
      startChat(finalForm);
    }
  };

  const handleVerify = async (e) => {
    const value = await e;
    setCaptchaSubmitted(value);
  };

  const handleSubmitOffline = () => {
    if (s360ConfigService.config?.sandBox) return;
    const errors = {};
    data[0].elements.forEach((field) => {
      const fieldname = field?.properties?.fieldname;
      const value = finalForm[fieldname];
      if (fieldname) {
        validateField(fieldname, value, field);
        if (field?.properties?.mandatory && !value?.trim()) {
          errors[fieldname] =
            field?.properties?.validationMessage ||
            `${field?.properties?.label} is required`;
        }
      }
    });

    setValidationErrors(errors);

    if (Object.keys(errors).length === 0) {
      if (s360ConfigService?.config?.businessHour?.offlineHour?.sendEmail) {
        actions
          .offlineFormSubmission(finalForm, captchaSubmitted)
          .then((res) => {
            setOfflineFormSubmitted(true);
          })
          .catch((err) => {
            console.warn(err);
            setCaptchaSubmitted(false);
          });
      } else {
        actions
          .offlineFormNoSendEmail(finalForm, captchaSubmitted)
          .then((res) => {
            setOfflineFormSubmitted(true);
          })
          .catch((err) => {
            setCaptchaSubmitted(false);
          });
      }
    }
  };

  const renderElements = (elements) => {
    return elements.map((element) => {
      if (element.type === "panel") {
        return renderElements(element.elements);
      }

      const commonInputProps = {
        field: element,
        handleInputChange,
        validationErrors,
        finalForm,
      };
      switch (element.type) {
        case "date":
          return <NCXFormDate {...commonInputProps} />;
        case "radio":
          return <NCXFormRadioButton {...commonInputProps} />;
        case "boolean":
          return <NCXFormBoolean {...commonInputProps} />;
        case "rate":
          return <NCXFormRating {...commonInputProps} />;
        // case "captcha":
        //   return (
        //     <NCXRecaptcha
        //       sitekey={siteKey}
        //       handleVerify={handleVerify}
        //       field={element}
        //     />
        //   );
        case "label":
          return <NCXFormLabel field={element} />;
        case "separator":
          return <NCXFormDivider field={element} />;
        case "select":
          return <NCXFormSelect {...commonInputProps} />;
        case "multiselect":
          return <NCXFormMultiSelect {...commonInputProps} />;
        case "textarea":
          return <NCXFormInputField {...commonInputProps} multiline />;
        case "rateagent":
          return (
            <NCXFormRateAgent {...commonInputProps} agentInfo={agentInfo} />
          );
        case "input":
        default:
          return <NCXFormInputField {...commonInputProps} />;
      }
    });
  };

  if (offlineFormSubmitted) {
    return (
      <div
        style={{
          padding: "2rem",
          background: "#ffffff",
          height: "100vh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {s360ConfigService?.config?.businessHour?.offlineHour?.completionMessage
          ? s360ConfigService?.config?.businessHour?.offlineHour
              ?.completionMessage
          : "Ticket raised successfully. We'll get in touch soon."}
      </div>
    );
  } else {
    return (
      <>
        {termsAndConditionsDecider && (
          <NCXPrivacyPolicyCompact
            config={
              s360ConfigService?.config?.configDetails?.privacyPolicyDetails
            }
            onAccept={() => {
              localStorage.setItem(
                `nextiva-widget-terms-acceptance-${s360ConfigService?.config?.widgetconfigId}`,
                "true"
              );
              setAcceptedPrivacy(true); // update immediately
            }}
          />
        )}
        <form
          style={{
            padding: "2rem 2rem 6rem 2rem",
            background: "#ffffff",
            height: "100vh",
            width: "100%",
            overflow: "scroll",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "white",
          }}
        >
          {offline ? (
            offlineForm ? (
              <div
                className="ticketForm-fields"
                style={{
                  overflowY: "scroll",
                  paddingTop: "10px",
                }}
              >
                {renderElements(data[0].elements)}

                {s360ConfigService?.config?.recaptchaSiteKey && (
                  <ReCAPTCHA
                    sitekey={s360ConfigService?.config?.recaptchaSiteKey}
                    onChange={(e) => handleVerify(e)}
                  />
                )}

                <div
                  style={{
                    position: "absolute",
                    bottom: "8px",
                    left: 0,
                    height: "35px",
                    width: "100%",
                    padding: "0 2rem",
                  }}
                >
                  <Button
                    disabled={!captchaSubmitted && !acceptedPrivacy}
                    variant="contained"
                    sx={{
                      background: configPrimaryColor,
                      color: configPrimaryFontColor,
                      fontSize: "1.2rem",
                      width: "inherit",
                      "&:hover": {
                        backgroundColor: configPrimaryColor,
                        color: configPrimaryFontColor,
                      },
                    }}
                    onClick={handleSubmitOffline}
                    data-testid={`${DATA_TEST_ID_PREFIX}-${LIVECHAT_CLICK_EVENTS.INITIAL_OFFLINE_FORM_SUBMIT}`}
                  >
                    Submit
                  </Button>
                </div>
              </div>
            ) : (
              <div className="offline-no-form">
                <div className="offline-no-form-text">
                  <h4>Offline Form</h4>
                  <p>There is no form available for offline mode.</p>
                </div>
              </div>
            )
          ) : (
            <div
              className="ticketForm-fields"
              style={{
                overflowY: "scroll",
                paddingTop: "10px",
              }}
            >
              {renderElements(data[0]?.elements)}

              {s360ConfigService?.config?.recaptchaSiteKey && (
                <ReCAPTCHA
                  sitekey={s360ConfigService?.config?.recaptchaSiteKey}
                  onChange={(e) => handleVerify(e)}
                />
              )}
              {s360ConfigService.config.sandBox ? (
                <div
                  style={{
                    position: "absolute",
                    bottom: "8px",
                    left: 0,
                    height: "35px",
                    width: "100%",
                    padding: "0 2rem",
                  }}
                >
                  <Button
                    variant="contained"
                    sx={{
                      background: configPrimaryColor,
                      color: configPrimaryFontColor,
                      fontSize: "1.2rem",
                      width: "inherit",
                      "&:hover": {
                        backgroundColor: configPrimaryColor,
                        color: configPrimaryFontColor,
                      },
                    }}
                  >
                    Submit
                  </Button>
                </div>
              ) : (
                <div
                  style={{
                    position: "absolute",
                    bottom: "8px",
                    left: 0,
                    height: "35px",
                    width: "100%",
                    padding: "0 2rem",
                  }}
                >
                  <Button
                    disabled={!captchaSubmitted && !acceptedPrivacy}
                    variant="contained"
                    sx={{
                      background: configPrimaryColor,
                      color: configPrimaryFontColor,
                      fontSize: "1.2rem",
                      width: "inherit",
                      "&:hover": {
                        backgroundColor: configPrimaryColor,
                        color: configPrimaryFontColor,
                      },
                    }}
                    onClick={handleSubmit}
                  >
                    Submit
                  </Button>
                </div>
              )}
            </div>
          )}
        </form>
      </>
    );
  }
};

export default DynamicForm;
